import React, { CSSProperties, FC, useCallback, useEffect, useState } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { BlockWithNotes } from '../../../../../../config/api/models/Block';
import { useFetch } from '../../../../../shared';
import apiRoutes from '../../../../../../config/api/routes';
import { postVideoEventMapper } from '../../../../../../config/api/models';
import { VideoEventType, VideoProgressData } from '../../../../../../config/api/models/VideoLog';
import { v4 as uuidv4 } from 'uuid';
import { Skeleton } from '@material-ui/lab';
import { ReactPlayerLoadingController } from './styled';

type Props = {
  block?: BlockWithNotes;
  className?: string;
  url?: string;
  height?: string;
  customStyle?: CSSProperties;
  hasLoading?: boolean;
  videoProps?: ReactPlayerProps;
};

const SESSION_ID: string = uuidv4();
const PROGRESS_INTERVAL: number = 250; // interval in ms in which progress is saved

const VideoPlayer: FC<Props> = ({
  block,
  className,
  url,
  height,
  customStyle = {},
  hasLoading = false,
  videoProps = {},
}) => {
  const [, postVideoEvent] = useFetch(apiRoutes.postVideoEvent(), {
    method: 'POST',
    mapper: postVideoEventMapper,
  });

  const [progressData, setProgressData] = useState<VideoProgressData>({
    playedSeconds: 0,
    played: 0,
    loadedSeconds: 0,
    loaded: 0,
  });
  const [loading, setLoading] = useState<boolean>(hasLoading);
  const [videoUrl, setVideoUrl] = useState<string | undefined>()

  const logPlayEvent = useCallback(() => {
    postVideoEvent({
      body: {
        event: VideoEventType.PLAY,
        played: progressData.played,
        played_seconds: progressData.playedSeconds,
        url: block?.content ?? url,
        session: SESSION_ID,
      },
    });
  }, [block?.content, postVideoEvent, progressData.played, progressData.playedSeconds, url]);

  const logPauseEvent = useCallback(() => {
    postVideoEvent({
      body: {
        event: VideoEventType.PAUSE,
        played: progressData.played,
        played_seconds: progressData.playedSeconds,
        url: block?.content ?? url,
        session: SESSION_ID,
      },
    });
  }, [block?.content, postVideoEvent, progressData.played, progressData.playedSeconds, url]);

  const logEndedEvent = useCallback(() => {
    postVideoEvent({
      body: {
        event: VideoEventType.ENDED,
        played: progressData.played,
        played_seconds: progressData.playedSeconds,
        url: block?.content ?? url,
        session: SESSION_ID,
      },
    });
  }, [block?.content, postVideoEvent, progressData.played, progressData.playedSeconds, url]);

  const handleReady = useCallback(() => {
    if (loading) {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    setVideoUrl((block?.content as string) ?? url);
  }, [url, block?.content])
  return (
    <>
      {loading && (
        <Skeleton
          variant="rect"
          width="100%"
          height={height ? height : 'auto'}
          style={{ aspectRatio: '16/9' }}
        />
      )}
      <ReactPlayerLoadingController loading={loading}>
        <ReactPlayer
          key={videoUrl}
          onReady={hasLoading ? handleReady : () => {}}
          style={customStyle}
          className={className}
          url={videoUrl}
          controls={true}
          width="100%"
          height={height ? height : '100%'}
          progressInterval={PROGRESS_INTERVAL}
          onPlay={logPlayEvent}
          onPause={logPauseEvent}
          onBuffer={logPauseEvent}
          onEnded={logEndedEvent}
          onProgress={(state) => setProgressData(state)}
          {...videoProps}
        />
      </ReactPlayerLoadingController>
    </>
  );
};

export default VideoPlayer;
